import { api } from './api';

// ── Spec 051 — customer invoice / credit-note admin endpoints (orders svc) ──

/**
 * Spec 051 issued the garage's documents (the garage is the supplier, Garagat invoices as agent).
 * Spec 053 Phase 3 / Flow F4 adds Garagat's own pair for the pickup & delivery supply:
 * PLATFORM_INVOICE (`PLT-YYYY-NNNNNN`) and PLATFORM_CREDIT_NOTE (`PCN-YYYY-NNNNNN`), issued with
 * Garagat as the supplier and Garagat's TRN. Both pairs live in the same `invoice_documents` table
 * and come back on the same endpoint, so the panel must tell them apart on screen (BR-24).
 */
export type InvoiceDocType = 'INVOICE' | 'CREDIT_NOTE' | 'PLATFORM_INVOICE' | 'PLATFORM_CREDIT_NOTE';

/** Who supplied what the document is for — the distinction an admin has to be able to read. */
export const INVOICE_DOC_TYPE_LABELS: Record<InvoiceDocType, string> = {
  INVOICE: 'Garage tax invoice',
  CREDIT_NOTE: 'Garage credit note',
  PLATFORM_INVOICE: 'Garagat tax invoice',
  PLATFORM_CREDIT_NOTE: 'Garagat credit note',
};

export const INVOICE_DOC_TYPE_SUPPLIERS: Record<InvoiceDocType, string> = {
  INVOICE: 'Supplied by the garage — service',
  CREDIT_NOTE: 'Supplied by the garage — service',
  PLATFORM_INVOICE: 'Supplied by Garagat — pickup & delivery',
  PLATFORM_CREDIT_NOTE: 'Supplied by Garagat — pickup & delivery',
};

/**
 * #1056 (Spec 068) — Garagat's own document (PLT / PCN) may carry the pickup & delivery line, the service-fee
 * line, or both, so its supplier line is built from what it carries. A garage document, or a response from an
 * orders build without the line amounts, keeps the fixed label.
 */
export function invoiceSupplierLabel(doc: Pick<AdminInvoiceDocument, 'docType' | 'deliveryFeeGross' | 'serviceFeeGross'>): string {
  const fixed = INVOICE_DOC_TYPE_SUPPLIERS[doc.docType as InvoiceDocType]
    ?? (isPlatformDocType(doc.docType) ? 'Supplied by Garagat' : 'Supplied by the garage');
  if (!isPlatformDocType(doc.docType) || doc.serviceFeeGross == null) {
    return fixed;
  }
  const hasFee = doc.serviceFeeGross > 0;
  const hasDelivery = (doc.deliveryFeeGross ?? 0) > 0;
  if (hasFee && hasDelivery) return 'Supplied by Garagat — pickup & delivery + service fee';
  if (hasFee) return 'Supplied by Garagat — service fee';
  return 'Supplied by Garagat — pickup & delivery';
}

export function isPlatformDocType(docType: string): boolean {
  return docType === 'PLATFORM_INVOICE' || docType === 'PLATFORM_CREDIT_NOTE';
}

export function isCreditNoteDocType(docType: string): boolean {
  return docType === 'CREDIT_NOTE' || docType === 'PLATFORM_CREDIT_NOTE';
}
/**
 * #466 adds ABANDONED: terminal, set by the orders sweeper when the order a document belongs to no
 * longer exists, so it can never be generated. Its reason is in `lastError`.
 */
export type InvoiceStatus = 'HELD_SUPPLIER_DATA' | 'PENDING_DOCUMENT' | 'ISSUED' | 'ABANDONED';
export type InvoiceDeliveryStatus =
  | 'PENDING'
  | 'SENT'
  | 'FAILED'
  | 'NO_EMAIL_ON_FILE'
  // #823 — reported by the provider AFTER it accepted the message. SENT only ever meant
  // "Resend took it"; these say what became of it.
  | 'DELAYED'
  | 'DELIVERED'
  | 'BOUNCED'
  | 'COMPLAINED'
  | 'SUPPRESSED';

export interface InvoiceDeliveryLogEntry {
  at: string;
  actor: string;
  recipient: string | null;
  result: string;
  error: string | null;
}

export interface AdminInvoiceDocument {
  id: string;
  docType: InvoiceDocType;
  invoiceNumber: string | null; // null while held (number assigned at issuance)
  status: InvoiceStatus;
  holdReason: string | null;
  issueDate: string | null;
  grossAmount: number;
  vatAmount: number;
  currency: string;
  originalInvoiceNumber: string | null; // credit note → its invoice (a PCN references its PLT)
  deliveryStatus: InvoiceDeliveryStatus;
  /** For BOUNCED/SUPPRESSED: true when resending to the SAME address cannot work (#823). */
  deliveryPermanent: boolean | null;
  sentTo: string | null;
  sentAt: string | null;
  lastError: string | null;
  /**
   * #466 — when the sweeper last raised INVOICE_STALE for this document, or null if it never has.
   * The marker fires once per staleness episode now, so this panel is where "the sweeper has
   * reported this one as stuck" has to be readable.
   */
  staleAlertedAt: string | null;
  deliveryLog: InvoiceDeliveryLogEntry[];
  /**
   * Spec 068 (#1056) — Garagat's lines on a PLT / PCN: the service fee (the slice refunded on a PCN) and the
   * pickup & delivery line. 0 when the document has no such line or is the garage's; absent from older builds.
   */
  serviceFeeGross?: number | null;
  deliveryFeeGross?: number | null;
}

export interface AdminInvoiceListResponse {
  documents: AdminInvoiceDocument[];
}

export interface InvoiceDownloadUrlResponse {
  downloadUrl: string;
  expiresInSeconds: number;
}

export interface ResendInvoiceRequest {
  emailOverride?: string | null;
}

export interface ResendInvoiceResult {
  // QUEUED: the email was handed to the notifications service; the outcome lands in the delivery log.
  // FAILED is immediate (e.g. no email on file). SENT is the legacy synchronous answer.
  result: 'QUEUED' | 'SENT' | 'FAILED';
  error: string | null;
}

export const adminOrderInvoicesApi = {
  getInvoices: (orderId: string, token: string) =>
    api.get<AdminInvoiceListResponse>(`/api/admin/orders/${orderId}/invoices`, token),

  // 409 INVOICE_NOT_READY while the document is not yet ISSUED.
  getDownloadUrl: (orderId: string, documentId: string, token: string) =>
    api.get<InvoiceDownloadUrlResponse>(
      `/api/admin/orders/${orderId}/invoices/${documentId}/download-url`,
      token,
    ),

  // Resends the SAME stored PDF; never regenerates (BR-11). Optional per-send
  // email override — never mutates the customer's profile email (BR-12).
  resend: (orderId: string, documentId: string, body: ResendInvoiceRequest, token: string) =>
    api.post<ResendInvoiceResult>(
      `/api/admin/orders/${orderId}/invoices/${documentId}/resend`,
      body,
      token,
    ),

  // Manual generation for paid pre-go-live orders (AC-11).
  // 201 document · 409 INVOICE_ALREADY_EXISTS · 422 order not paid.
  generate: (orderId: string, token: string) =>
    api.post<AdminInvoiceDocument>(`/api/admin/orders/${orderId}/invoices/generate`, {}, token),
};
